// 未使用のimportを削除
import apiClient from './apiClient';
import { Patient, PatientListResponse, PatientSearchParams } from '../types/patient';
// 外部データベースのAPIエンドポイント
const BASE_URL = '/external/patients';
// レスポンスを処理する関数
const processResponse = (data: any): PatientListResponse => {
  // レスポンスがページネーション形式の場合
  if (data && ('results' in data)) {
    return data;
  }
  // 配列の場合（ページネーションなし）
  else if (Array.isArray(data)) {
    const patients = data as Patient[];
    return {
      count: patients.length,
      next: null,
      previous: null,
      results: patients
    };
  }
  // その他の形式
  else {
    console.warn('不明なレスポンス形式:', data);
    return {
      count: 0,
      next: null,
      previous: null,
      results: []
    };
  }
};
// 患者一覧を取得
export const getPatients = async(params: PatientSearchParams = {}): Promise<PatientListResponse> => {
  // パラメータを加工
  const queryParams = { ...params };
  // 退会済みのみモードを処理
  if (queryParams.include_resigned === 'only') {
    // バックエンドの実装に合わせて値を設定
    queryParams.resigned_only = 'true';
    queryParams.include_resigned = 'true';
  }
  try {
    // role_typeが配列の場合、URLクエリパラメータを正しく処理
    if (queryParams.role_type && Array.isArray(queryParams.role_type) && queryParams.role_type.length > 0) {
      // 各ロールタイプを別々のパラメータとして追加
      const roleTypes = queryParams.role_type;
      delete queryParams.role_type;
      // URLSearchParamsを使用してパラメータを生成
      const urlParams = new URLSearchParams();
      // ロール以外のパラメータをコピー
      Object.entries(queryParams).forEach(([ key, value ]) => {
        if (value !== undefined) {
          urlParams.append(key, String(value));
        }
      });
      // DRF標準の'in'フィルターに対応（カンマ区切りのリストとして送信）
      urlParams.append('role_type__in', roleTypes.join(','));
      const fullUrl = `${BASE_URL}?${urlParams.toString()}`;
      try {
        // URLSearchParamsを使用してGETリクエストを送信
        const response = await apiClient.get<PatientListResponse>(fullUrl);
        return processResponse(response.data);
      } catch (error) {
        // エラーがページネーションに関連する場合は、ページをリセットして再試行
        console.error('API呼び出しエラー:', error);
        // ページネーションを1ページ目にリセット
        urlParams.set('page', '1');
        const retryUrl = `${BASE_URL}?${urlParams.toString()}`;
        const retryResponse = await apiClient.get<PatientListResponse>(retryUrl);
        return processResponse(retryResponse.data);
      }
    } else {
      // 通常のパラメータ処理
      try {
        const response = await apiClient.get<PatientListResponse>(BASE_URL, { params: queryParams });
        return processResponse(response.data);
      } catch (error) {
        // エラーがページネーションに関連する場合は、ページをリセットして再試行
        console.error('API呼び出しエラー:', error);
        // パラメータを複製して変更
        const retryParams = { ...queryParams, page: 1 };
        const retryResponse = await apiClient.get<PatientListResponse>(BASE_URL, { params: retryParams });
        return processResponse(retryResponse.data);
      }
    }
  } catch (error) {
    console.error('API呼び出しエラー:', error);
    throw error;
  }
};
// 患者詳細を取得
export const getPatient = async(id: number): Promise<Patient> => {
  const response = await apiClient.get<Patient>(`${BASE_URL}/${id}/`);
  return response.data;
};
// 患者メモを更新
export const updatePatientMemo = async(id: number, memo: string): Promise<Patient> => {
  const response = await apiClient.put<Patient>(`${BASE_URL}/${id}/`, { senior_memo: memo });
  return response.data;
};

// パスワードリセットURLを生成
export const generatePasswordResetUrl = async(id: number, withNotification: boolean): Promise<{
  reset_url: string;
  expires_at: string;
}> => {
  const response = await apiClient.post(`${BASE_URL}/${id}/password_reset/`, { 
    with_notification: withNotification 
  });
  return response.data;
};

// CSV出力用のパラメータ型
export interface CSVExportParams extends PatientSearchParams {
  exclude_fields?: string[];
}

// 患者一覧CSVプレビューを取得
export const getPatientsCSVPreview = async(params: PatientSearchParams = {}): Promise<{
  headers: Array<{ field: string; display: string }>;
  all_headers: Array<{ field: string; display: string }>;
  personal_fields: string[];
  rows: Array<Record<string, any>>;
  total_count: number;
}> => {
  // output_format を追加
  const csvParams = { ...params, output_format: 'csv_preview', preview_rows: 10 };
  // role_typeが配列の場合の処理
  if (csvParams.role_type && Array.isArray(csvParams.role_type) && csvParams.role_type.length > 0) {
    const roleTypes = csvParams.role_type;
    delete csvParams.role_type;
    const urlParams = new URLSearchParams();
    // ロール以外のパラメータをコピー
    Object.entries(csvParams).forEach(([ key, value ]) => {
      if (value !== undefined && value !== null && value !== '') {
        urlParams.append(key, String(value));
      }
    });
    // role_type__in として送信
    urlParams.append('role_type__in', roleTypes.join(','));
    const fullUrl = `${BASE_URL}/?${urlParams.toString()}`;
    const response = await apiClient.get(fullUrl);
    return response.data;
  } else {
    const response = await apiClient.get(`${BASE_URL}/`, { params: csvParams });
    return response.data;
  }
};
const patientsApi = {
  getPatients,
  getPatient,
  updatePatientMemo,
  getPatientsCSVPreview,
};
export default patientsApi;
